import { useParams, Link } from 'react-router-dom';
import { useSharedArticle } from '../hooks/useDeepLink';
import { useAuth } from '../hooks/useAuth';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

export function SharedArticlePage() {
  const { token } = useParams<{ token: string }>();
  const { data: article, loading, error } = useSharedArticle(token);
  const { user } = useAuth();

  // Set document meta tags for SEO and browser tab
  useDocumentMeta({
    title: article ? `${article.title} | Read l8r` : 'Shared Article | Read l8r',
    description: article?.excerpt || 'Read this article shared via Read l8r',
    ogTitle: article?.title,
    ogDescription: article?.excerpt,
    ogType: 'article',
    ogUrl: token ? `${window.location.origin}/s/${token}` : undefined,
    twitterCard: 'summary_large_image'
  });

  if (loading) {
    return (
      <div className="shared-page loading">
        <div className="spinner" />
        <p>Loading shared article...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="shared-page error">
        <div className="error-icon">🔗</div>
        <h1>Link Not Found</h1>
        <p>{error || 'This shared article could not be found.'}</p>
        <Link to="/" className="btn-primary">Go to Read l8r</Link>
      </div>
    );
  }

  return (
    <div className="shared-page">
      {/* Header */}
      <header className="shared-header">
        <Link to="/" className="shared-logo">
          <span>📖</span> Read l8r
        </Link>
        <div className="shared-actions">
          {user ? (
            <button className="btn-primary btn-save" onClick={() => handleSaveToLibrary()}>
              Save to Library
            </button>
          ) : (
            <Link to="/app" className="btn-primary">
              Sign up to save
            </Link>
          )}
        </div>
      </header>

      {/* Shared by info */}
      {article.sharedBy && (
        <div className="shared-by">
          {article.sharedBy.avatar && (
            <img src={article.sharedBy.avatar} alt="" className="sharer-avatar" />
          )}
          <span>Shared by <strong>{article.sharedBy.name}</strong></span>
        </div>
      )}

      {/* Article content */}
      <article className="shared-article">
        <h1>{article.title}</h1>

        <div className="article-meta">
          <span>{article.wordCount.toLocaleString()} words</span>
          <span>•</span>
          <span>{article.readingTime} min read</span>
          {article.url && (
            <>
              <span>•</span>
              <a href={article.url} target="_blank" rel="noopener noreferrer" className="source-link">
                View original ↗
              </a>
            </>
          )}
        </div>

        {/* Highlights */}
        {article.highlights && article.highlights.length > 0 && (
          <div className="shared-highlights">
            <h2>Highlights</h2>
            {article.highlights.map((h, i) => (
              <blockquote key={i} className="highlight-block">
                <p>{h.text}</p>
                {h.note && <footer className="highlight-note">Note: {h.note}</footer>}
              </blockquote>
            ))}
          </div>
        )}

        {/* Article body */}
        <div
          className="article-body"
          dangerouslySetInnerHTML={{ __html: sanitizeContent(article.content) }}
        />
      </article>

      {/* CTA Footer */}
      <div className="shared-cta">
        <h2>Save articles to read later</h2>
        <p>Read l8r helps you save, organize, and listen to articles from anywhere.</p>
        <Link to="/app" className="btn-primary btn-large">
          Get Started Free
        </Link>
      </div>

      <style>{sharedPageStyles}</style>
    </div>
  );

  async function handleSaveToLibrary() {
    // TODO: Implement save to library
    alert('Save to library coming soon!');
  }
}

function sanitizeContent(html: string): string {
  // Basic sanitization - in production use DOMPurify
  const div = document.createElement('div');
  div.innerHTML = html;

  // Remove scripts
  div.querySelectorAll('script, style, iframe').forEach(el => el.remove());

  // Remove event handlers
  div.querySelectorAll('*').forEach(el => {
    Array.from(el.attributes).forEach(attr => {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return div.innerHTML;
}

const sharedPageStyles = `
  .shared-page {
    min-height: 100vh;
    background: var(--landing-bg, #fafaf9);
    color: var(--landing-text, #1c1917);
  }

  @media (prefers-color-scheme: dark) {
    .shared-page {
      background: #0a0a0a;
      color: #fafaf9;
    }
  }

  .shared-page.loading,
  .shared-page.error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    text-align: center;
  }

  .error-icon {
    font-size: 4rem;
    opacity: 0.5;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(249, 115, 22, 0.2);
    border-top-color: #f97316;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .shared-header {
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: rgba(250, 250, 249, 0.9);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--landing-border, #e7e5e4);
  }

  @media (prefers-color-scheme: dark) {
    .shared-header {
      background: rgba(10, 10, 10, 0.9);
      border-color: #27272a;
    }
  }

  .shared-logo {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
    font-size: 1.25rem;
    color: inherit;
    text-decoration: none;
  }

  .shared-by {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    background: rgba(249, 115, 22, 0.1);
    font-size: 0.875rem;
  }

  .sharer-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }

  .shared-article {
    max-width: 680px;
    margin: 0 auto;
    padding: 2rem 1.5rem;
  }

  .shared-article h1 {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 1rem;
  }

  .article-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--landing-text-secondary, #78716c);
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--landing-border, #e7e5e4);
  }

  .source-link {
    color: #f97316;
    text-decoration: none;
  }

  .source-link:hover {
    text-decoration: underline;
  }

  .shared-highlights {
    background: rgba(249, 115, 22, 0.05);
    border-left: 3px solid #f97316;
    padding: 1.5rem;
    margin-bottom: 2rem;
    border-radius: 0 12px 12px 0;
  }

  .shared-highlights h2 {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #f97316;
  }

  .highlight-block {
    margin: 0 0 1rem 0;
    padding: 0;
    border: none;
    font-style: normal;
  }

  .highlight-block p {
    margin: 0;
    line-height: 1.6;
  }

  .highlight-note {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: var(--landing-text-secondary, #78716c);
    font-style: italic;
  }

  .article-body {
    font-family: Georgia, serif;
    font-size: 1.125rem;
    line-height: 1.8;
  }

  .article-body p {
    margin-bottom: 1.5em;
  }

  .article-body h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 2em 0 0.75em;
  }

  .article-body h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 1.5em 0 0.5em;
  }

  .article-body blockquote {
    border-left: 3px solid #f97316;
    padding-left: 1rem;
    margin: 1.5rem 0;
    font-style: italic;
    color: var(--landing-text-secondary, #78716c);
  }

  .article-body img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1.5rem 0;
  }

  .shared-cta {
    text-align: center;
    padding: 4rem 1.5rem;
    background: linear-gradient(135deg, #f97316 0%, #fb923c 100%);
    color: white;
    margin-top: 3rem;
  }

  .shared-cta h2 {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .shared-cta p {
    opacity: 0.9;
    margin-bottom: 1.5rem;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #f97316;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    text-decoration: none;
    border: none;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-primary:hover {
    background: #ea580c;
  }

  .shared-cta .btn-primary {
    background: white;
    color: #f97316;
  }

  .shared-cta .btn-primary:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  .btn-large {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }

  @media (max-width: 600px) {
    .shared-article h1 {
      font-size: 1.5rem;
    }

    .article-body {
      font-size: 1rem;
    }

    .shared-cta h2 {
      font-size: 1.5rem;
    }
  }
`;
